import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileCode2, Map, Bot, Database, Shuffle, Wand2, ShieldOff, Zap } from 'lucide-react';

// 3.30 — NativeStackSection: visual deep-dive of the 7-layer Native Stack
// For technical visitors who need explanation power
const NativeStackSection = () => {
  const { t } = useTranslation();

  const layers = [
    {
      id: 'meta',
      icon: FileCode2,
      color: '#0055FF',
      replaces: 'Yoast head',
    },
    {
      id: 'sitemap',
      icon: Map,
      color: '#06B6D4',
      replaces: 'Yoast sitemap',
    },
    {
      id: 'llms',
      icon: Bot,
      color: '#10B981',
      badge: 'unique',
    },
    {
      id: 'schema',
      icon: Database,
      color: '#8B5CF6',
      replaces: 'Yoast schema',
    },
    {
      id: 'redirect',
      icon: Shuffle,
      color: '#F59E0B',
      replaces: 'Redirection plugin',
    },
    {
      id: 'ai404',
      icon: Wand2,
      color: '#EC4899',
      badge: 'ai',
    },
    {
      id: 'override',
      icon: ShieldOff,
      color: '#EF4444',
      badge: 'safe',
    },
  ];

  return (
    <section className="py-24 px-6 bg-[#0a0a0a] border-t border-white/5 relative overflow-hidden" data-testid="native-stack-section">
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-[#00E5FF]/8 rounded-full blur-[120px] pointer-events-none -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-72 h-72 bg-[#0055FF]/6 rounded-full blur-[120px] pointer-events-none -translate-y-1/2" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-14 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 mb-6">
            <Zap className="w-4 h-4 text-[#00E5FF]" />
            <span className="text-sm text-[#67e8f9]">{t('nativeStack.badge')}</span>
          </div>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mb-6">
            {t('nativeStack.title1')}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00E5FF] to-[#0055FF]">
              {t('nativeStack.title2')}
            </span>
          </h2>
          <p className="text-lg text-[#A1A1AA] max-w-3xl mx-auto leading-relaxed">
            {t('nativeStack.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left: visual stack */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="rounded-2xl border border-white/10 bg-black/40 p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-semibold mb-4">
                {t('nativeStack.stackLabel')}
              </div>
              <div className="space-y-2">
                {layers.map((l, idx) => {
                  const Icon = l.icon;
                  return (
                    <div
                      key={l.id}
                      className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5 flex items-center gap-3 hover:border-white/20 transition-colors"
                      style={{ borderLeftColor: l.color, borderLeftWidth: 3 }}
                      data-testid={`stack-layer-${l.id}`}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${l.color}1a` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: l.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">
                          {t(`nativeStack.layers.${l.id}.title`)}
                        </div>
                        <div className="text-[11px] text-[#A1A1AA] truncate">
                          {t(`nativeStack.layers.${l.id}.short`)}
                        </div>
                      </div>
                      {l.badge === 'unique' && (
                        <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-[#10B981] to-[#00E5FF] text-black">
                          {t('nativeStack.badgeUnique')}
                        </span>
                      )}
                      {l.badge === 'ai' && (
                        <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#EC4899]/20 text-[#f472b6]">
                          AI
                        </span>
                      )}
                      {l.badge === 'safe' && (
                        <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#EF4444]/20 text-[#fca5a5]">
                          {t('nativeStack.badgeSafe')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: detailed descriptions */}
          <div className="lg:col-span-7 order-1 lg:order-2 space-y-3">
            {layers.map((l, idx) => {
              const Icon = l.icon;
              return (
                <div
                  key={l.id}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 transition-colors animate-fade-up"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${l.color}1a`, border: `1px solid ${l.color}30` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: l.color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <h4 className="text-base font-semibold text-white">
                          {t(`nativeStack.layers.${l.id}.title`)}
                        </h4>
                        {l.replaces && (
                          <span className="text-[10px] uppercase tracking-wider text-[#71717A] bg-white/5 px-2 py-0.5 rounded-full">
                            {t('nativeStack.replacesLabel')}: {l.replaces}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#A1A1AA] leading-relaxed">
                        {t(`nativeStack.layers.${l.id}.desc`)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 text-center text-sm text-[#71717A] animate-fade-up">
          {t('nativeStack.footer')}
        </div>
      </div>
    </section>
  );
};

export default NativeStackSection;
